/**
 * Sterling storefront ↔ productauctions.herokuapp.com helpers.
 * Config: snippets/sta-auctions-config.liquid (window.STA_AUCTIONS + localStorage).
 */
(function () {
  var DEFAULT_BASE = 'https://productauctions.herokuapp.com';
  var DEFAULT_PROXY_BASE = 'https://app.sterlingauctions.com';

  function normalizeBase(url) {
    return String(url || '').replace(/\/+$/, '');
  }

  window.staAuctionsBase = function () {
    if (window.STA_AUCTIONS && window.STA_AUCTIONS.base) {
      return normalizeBase(window.STA_AUCTIONS.base);
    }
    var raw = localStorage.getItem('sterlings_base_url');
    if (raw) return normalizeBase(raw);
    return DEFAULT_BASE;
  };

  window.staAuctionsPublicKey = function () {
    if (window.STA_AUCTIONS && window.STA_AUCTIONS.publicKey) {
      return window.STA_AUCTIONS.publicKey;
    }
    return localStorage.getItem('sterlings_public_key') || '';
  };

  /** Bid proxy base (sterling-auctions-shopify) — avoids CORS on POST max_bids. */
  window.staAuctionsProxyBase = function () {
    if (window.STA_AUCTIONS && window.STA_AUCTIONS.proxyBase) {
      return normalizeBase(window.STA_AUCTIONS.proxyBase);
    }
    var raw = localStorage.getItem('sterlings_proxy_base');
    if (raw) return normalizeBase(raw);
    return DEFAULT_PROXY_BASE;
  };

  /**
   * Returning a visitor to the lot they left.
   *
   * Bidding sends people away from a lot twice — to sign in, and to verify —
   * and both times they should come back to the same lot, because that is the
   * moment they want to bid. Every button that leaves a lot passes the lot's
   * path (including ?auction_id=) as `return_to`, and each destination sends
   * the visitor there when it is done.
   *
   * Only paths on this site are ever accepted. A return parameter that will
   * redirect anywhere is a phishing tool: a link to the real login page that
   * lands the victim on a lookalike site afterwards.
   */
  var RETURN_PARAM = 'return_to';

  function isSafeReturnPath(value) {
    if (typeof value !== 'string' || value.charAt(0) !== '/') return false;
    try {
      // Resolving catches "//evil.example" and "/\evil.example", which browsers
      // treat as other hosts despite starting with a slash.
      return new URL(value, window.location.origin).origin === window.location.origin;
    } catch (e) {
      return false;
    }
  }

  function withReturn(url, path) {
    if (!isSafeReturnPath(path)) return url;
    var separator = url.indexOf('?') === -1 ? '?' : '&';
    return url + separator + RETURN_PARAM + '=' + encodeURIComponent(path);
  }

  window.staReturn = {
    isSafe: isSafeReturnPath,
    withReturn: withReturn,

    /** Where the visitor is now, query string included. */
    currentPath: function () {
      return window.location.pathname + window.location.search;
    },

    /** The validated return path on the current page, or null. */
    read: function () {
      try {
        var value = new URL(window.location.href).searchParams.get(RETURN_PARAM);
        return isSafeReturnPath(value) ? value : null;
      } catch (e) {
        return null;
      }
    },

    loginUrl: function (path) {
      var routes = window.STA_ROUTES || {};
      return withReturn(routes.login || '/customer_authentication/login', path);
    },

    verifyUrl: function (path) {
      var routes = window.STA_ROUTES || {};
      return withReturn(routes.verify || '/pages/verify-to-bid', path);
    },
  };

  /**
   * Sign-in links marked data-sta-login-return are rendered by Liquid with the
   * page path, but Liquid cannot see the query string — and ?auction_id= is part
   * of the lot. This fills in the full path once the page is running.
   */
  document.addEventListener('DOMContentLoaded', function () {
    var links = document.querySelectorAll('[data-sta-login-return]');
    Array.prototype.forEach.call(links, function (link) {
      link.href = window.staReturn.loginUrl(window.staReturn.currentPath());
    });
  });

  /** App Proxy root. Same-origin, so Shopify signs the request for us. */
  window.STA_PROXY_PATH = '/apps/verification';

  /**
   * POST a max bid through the verification gate.
   *
   * Goes to the App Proxy rather than straight to the app's own domain, which
   * matters for more than tidiness: Shopify signs proxied requests and appends
   * the real logged_in_customer_id, so the server knows who is bidding without
   * trusting anything this script says. The customer id below is sent only for
   * backwards compatibility — the gate ignores it.
   */
  window.staPostMaxBid = function (auctionId, body) {
    var payload = Object.assign({}, body, { auction_id: String(auctionId) });

    return fetch(window.STA_PROXY_PATH + '/bid', {
      method: 'POST',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      // same-origin: the session cookie is what lets Shopify identify the customer
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    }).then(function (r) {
      return r.json().then(function (data) {
        return { ok: r.ok, status: r.status, data: data };
      });
    });
  };

  /**
   * Is the signed-in shopper allowed to bid? Resolves null when unknown (signed
   * out, or the app is unreachable) so callers can leave the UI alone rather
   * than wrongly telling someone they need to verify.
   */
  window.staBidderStatus = function () {
    return fetch(window.STA_PROXY_PATH, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(function (r) {
        if (!r.ok) return null;
        return r.json();
      })
      .then(function (payload) {
        if (!payload || !payload.ok || !payload.data) return null;
        return payload.data;
      })
      .catch(function () {
        return null;
      });
  };

  window.staAuctionHeaders = function (extra) {
    var h = Object.assign({ Accept: 'application/json' }, extra || {});
    var key = window.staAuctionsPublicKey();
    if (key) h['X-Public-Key'] = key;
    if (/ngrok/.test(window.staAuctionsBase())) {
      h['ngrok-skip-browser-warning'] = 'true';
    }
    return h;
  };

  window.staAuctionFetch = function (path, options) {
    var base = window.staAuctionsBase();
    var url =
      path.indexOf('http://') === 0 || path.indexOf('https://') === 0
        ? path
        : base + path;
    var opts = Object.assign({}, options || {});
    opts.headers = Object.assign({}, window.staAuctionHeaders(), opts.headers || {});
    if (!opts.mode) opts.mode = 'cors';
    if (!opts.credentials) opts.credentials = 'omit';
    return fetch(url, opts);
  };

  window.staAuctionFetchJson = function (path, options) {
    return window.staAuctionFetch(path, options).then(function (r) {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    });
  };

  window.staPickAuctionArray = function (payload) {
    if (Array.isArray(payload && payload.items)) return payload.items;
    if (Array.isArray(payload && payload.data)) return payload.data;
    if (Array.isArray(payload && payload.data && payload.data.data)) {
      return payload.data.data;
    }
    if (Array.isArray(payload)) return payload;
    return [];
  };

  window.staAuctionRowAttrs = function (row) {
    return row && row.attributes ? row.attributes : row || {};
  };

  window.staIsExcludedAuctionStatus = function (attrs) {
    return /disabled|ended|archive|complete|cancel/.test(
      String((attrs && attrs.status) || '').toLowerCase()
    );
  };

  window.staIsLiveAuctionRow = function (row, nowMs) {
    var now = nowMs != null ? nowMs : Date.now();
    var a = window.staAuctionRowAttrs(row);
    if (window.staIsExcludedAuctionStatus(a)) return false;
    var sd = Date.parse(a.start_date || a.start_at || '');
    var ed = Date.parse(a.end_date || a.end_at || '');
    return (
      Number.isFinite(sd) &&
      Number.isFinite(ed) &&
      sd <= now &&
      now < ed
    );
  };

  window.staIsPastAuctionRow = function (row, nowMs) {
    var now = nowMs != null ? nowMs : Date.now();
    var a = window.staAuctionRowAttrs(row);
    if (window.staIsExcludedAuctionStatus(a)) return true;
    var ed = Date.parse(a.end_date || a.end_at || '');
    return Number.isFinite(ed) && ed <= now;
  };

  window.staIsUpcomingAuctionRow = function (row, nowMs) {
    var now = nowMs != null ? nowMs : Date.now();
    var a = window.staAuctionRowAttrs(row);
    if (window.staIsExcludedAuctionStatus(a)) return false;
    var sd = Date.parse(a.start_date || a.start_at || '');
    var ed = Date.parse(a.end_date || a.end_at || '');
    return Number.isFinite(sd) && Number.isFinite(ed) && now < sd && now < ed;
  };

  window.staHasAuctionWinner = function (attrs) {
    if (!attrs) return false;
    var winnerRaw =
      attrs.winner_id != null
        ? attrs.winner_id
        : attrs.winner_customer_id != null
          ? attrs.winner_customer_id
          : attrs.winning_bid_id;
    return (
      winnerRaw != null &&
      String(winnerRaw).trim() !== '' &&
      String(winnerRaw).toLowerCase() !== 'null' &&
      String(winnerRaw).trim() !== '0'
    );
  };

  /**
   * A finished lot is marked sold immediately but stays in its catalogue for 24h before
   * the daily sweep moves it to Archive/Buy Now, so bidders can see how it closed without
   * hunting for it. Keep this window in step with READY_TO_MOVE_AFTER_MS in the app's
   * sweep-ready-to-move job.
   */
  var STA_ENDED_LOT_HOLD_MS = 24 * 60 * 60 * 1000;

  /**
   * The same window, for callers holding a timestamp rather than an API row --
   * the catalogue cards count lots off the hidden product map, not off the API.
   * Exposed so nothing has to hard-code a second copy of the 24h that would then
   * drift out of step with this one.
   */
  window.staEndedLotHoldMs = STA_ENDED_LOT_HOLD_MS;

  /** Withdrawn outright — never resurface, even inside the hold window. */
  window.staIsWithdrawnAuctionStatus = function (attrs) {
    return /archive|cancel/.test(
      String((attrs && attrs.status) || '').toLowerCase()
    );
  };

  /** Ended, but still inside the 24h catalogue hold. */
  window.staIsRecentlyEndedAuctionRow = function (row, nowMs) {
    var now = nowMs != null ? nowMs : Date.now();
    var a = window.staAuctionRowAttrs(row);
    if (window.staIsWithdrawnAuctionStatus(a)) return false;
    var ed = Date.parse(a.end_date || a.end_at || '');
    return (
      Number.isFinite(ed) && ed <= now && now - ed < STA_ENDED_LOT_HOLD_MS
    );
  };

  /** Ended, completed, or winner-defined — hide from live catalogue grids. */
  window.staIsExcludedLiveCatalogueRow = function (row, nowMs) {
    var now = nowMs != null ? nowMs : Date.now();
    var a = window.staAuctionRowAttrs(row);

    // Inside the hold a just-finished lot keeps its place, so the ended/winner checks
    // below must not strip it back out.
    if (window.staIsRecentlyEndedAuctionRow(row, now)) return false;

    if (a.auction_has_ended === true) return true;
    if (window.staHasAuctionWinner(a)) return true;
    if (window.staIsPastAuctionRow(row, now)) return true;

    return false;
  };

  /** Live catalogue pages: live, upcoming, or inside the 24h ended-lot hold. */
  window.staShouldShowLiveCatalogueRow = function (row, nowMs) {
    var now = nowMs != null ? nowMs : Date.now();
    if (window.staIsExcludedLiveCatalogueRow(row, now)) return false;
    return (
      window.staIsLiveAuctionRow(row, now) ||
      window.staIsUpcomingAuctionRow(row, now) ||
      window.staIsRecentlyEndedAuctionRow(row, now)
    );
  };

  window.staFilterPastAuctionRows = function (list, nowMs) {
    return (list || []).filter(function (row) {
      return window.staIsPastAuctionRow(row, nowMs);
    });
  };

  window.staFilterUpcomingAuctionRows = function (list, nowMs) {
    return (list || []).filter(function (row) {
      return window.staIsUpcomingAuctionRow(row, nowMs);
    });
  };

  /**
   * True when another page of GET /api/auctions results likely exists.
   * Uses API meta when present; otherwise assumes a full page means more.
   */
  window.staAuctionsHasMorePages = function (payload, page, rowCount, perPage) {
    var meta = payload && payload.meta;
    var pp = perPage;
    if (meta) {
      if (meta.per_page != null) pp = Number(meta.per_page) || pp;
      var total = Number(meta.filtered_total);
      var cp = Number(meta.current_page) || page;
      if (Number.isFinite(total) && total >= 0 && pp > 0) {
        return cp * pp < total;
      }
    }
    return rowCount >= pp;
  };

  /**
   * Fetches all pages from GET /api/auctions.
   * Pass productIds to use ?product_ids= (do not combine with auction_ids).
   */
  window.staFetchAuctionsAllPages = function (opts) {
    opts = opts || {};
    var perPage = opts.perPage != null ? Number(opts.perPage) : 50;
    var maxPages = opts.maxPages != null ? Number(opts.maxPages) : 80;
    var productIds = opts.productIds;
    var ids = [];
    if (productIds) {
      ids = (Array.isArray(productIds) ? productIds : [productIds])
        .map(function (id) {
          return window.staProductNumericId(id);
        })
        .filter(Boolean);
    }

    function fetchPage(page) {
      if (ids.length && window.staFetchAuctionsByProductIds) {
        return window.staFetchAuctionsByProductIds(ids, { page: page, perPage: perPage });
      }
      var query =
        '?page=' +
        encodeURIComponent(String(page)) +
        '&per_page=' +
        encodeURIComponent(String(perPage));
      return window.staAuctionFetchJson('/api/auctions' + query);
    }

    var combined = [];
    var page = 1;

    function loadNext() {
      if (page > maxPages) return Promise.resolve(combined);
      return fetchPage(page).then(function (payload) {
        var rows = window.staPickAuctionArray(payload);
        combined = combined.concat(rows);
        if (
          !rows.length ||
          !window.staAuctionsHasMorePages(payload, page, rows.length, perPage)
        ) {
          return combined;
        }
        page += 1;
        return loadNext();
      });
    }

    return loadNext();
  };

  /** Past rows for optional product id filter; paginates through the API. */
  window.staFetchPastAuctionRows = function (opts) {
    return window.staFetchAuctionsAllPages(opts).then(function (rows) {
      var past = window.staFilterPastAuctionRows(rows);
      return { data: past };
    });
  };

  /** Mimics legacy Node middleware GET /api/auctions/past response shape. */
  window.staFetchPastAuctionsPayload = function () {
    return window.staFetchPastAuctionRows();
  };

  /** Mimics legacy GET /api/auctions/upcoming — client-side filter on index. */
  window.staFetchUpcomingAuctionsPayload = function () {
    return window.staAuctionFetchJson('/api/auctions').then(function (payload) {
      var rows = window.staFilterUpcomingAuctionRows(window.staPickAuctionArray(payload));
      return { data: rows };
    });
  };

  /** Numeric Shopify product id from API platform_product_id (GID or raw id). */
  window.staProductNumericId = function (platformProductId) {
    if (platformProductId == null || platformProductId === '') return '';
    var s = String(platformProductId).trim();
    var m = s.match(/(\d+)\s*$/);
    return m ? m[1] : s;
  };

  /** Logged-in Shopify customer id when available. */
  window.staCurrentCustomerId = function () {
    // Set by sta-auctions-config on every template; the element lookups below
    // only resolve on the PDP and the account pages.
    if (window.STA_CUSTOMER_ID) return String(window.STA_CUSTOMER_ID).trim();

    var btn = document.querySelector('[data-customer-id]');
    if (btn) {
      var fromBtn = (btn.getAttribute('data-customer-id') || '').trim();
      if (fromBtn) return fromBtn;
    }
    if (window.Shopify && window.Shopify.customer && window.Shopify.customer.id) {
      return String(window.Shopify.customer.id);
    }
    var root = document.getElementById('sta-account-root');
    if (root) {
      var fromRoot = (root.getAttribute('data-customer-id') || '').replace(/"/g, '').trim();
      if (fromRoot) return fromRoot;
    }
    return '';
  };

  /**
   * Resolve auction id from ?auction_id= or product metafield on product-info.
   */
  window.staResolveAuctionId = function () {
    try {
      var urlId = new URL(location.href).searchParams.get('auction_id');
      if (urlId && String(urlId).trim()) return String(urlId).trim();
    } catch (e) {}
    var productInfo = document.querySelector('product-info');
    if (productInfo) {
      var metaId = (productInfo.getAttribute('data-auction-api-id') || '').trim();
      if (metaId) return metaId;
    }
    return '';
  };

  /** GET /api/auctions/:id with optional platform_customer_id for max bid. */
  window.staAuctionShowPath = function (auctionId, customerId) {
    var path = '/api/auctions/' + encodeURIComponent(String(auctionId));
    if (customerId) {
      path += '?platform_customer_id=' + encodeURIComponent(String(customerId));
    }
    return path;
  };

  /**
   * GET /api/auctions?product_ids=1,2,3&page=&per_page=&platform_customer_id=
   * product_ids must be numeric Shopify product ids (comma-separated).
   */
  window.staFetchAuctionsByProductIds = function (productIds, opts) {
    opts = opts || {};
    var ids = (Array.isArray(productIds) ? productIds : [productIds])
      .map(function (id) {
        return window.staProductNumericId(id);
      })
      .filter(Boolean);
    if (!ids.length) return Promise.resolve({ data: [] });

    var query = '?product_ids=' + encodeURIComponent(ids.join(','));
    if (opts.page != null) query += '&page=' + encodeURIComponent(String(opts.page));
    if (opts.perPage != null) {
      query += '&per_page=' + encodeURIComponent(String(opts.perPage));
    }
    if (opts.customerId) {
      query += '&platform_customer_id=' + encodeURIComponent(String(opts.customerId));
    }
    return window.staAuctionFetchJson('/api/auctions' + query);
  };

  /** Dollars from data-store-price-cents (product-info or hidden map li). */
  window.staStorePriceDollars = function (productIdOrCents) {
    if (productIdOrCents != null && typeof productIdOrCents === 'object') {
      var el = productIdOrCents;
      var raw = el.getAttribute ? el.getAttribute('data-store-price-cents') : '';
      if (raw == null || raw === '') return null;
      var sc = parseInt(raw, 10);
      return isFinite(sc) && sc > 0 ? sc / 100 : null;
    }
    if (productIdOrCents != null && productIdOrCents !== '') {
      var id = String(productIdOrCents).trim();
      var li = document.querySelector(
        'ul.urls-check-cards-products li[data-product-id="' + id + '"]'
      );
      if (li) return window.staStorePriceDollars(li);
    }
    var cents = parseInt(productIdOrCents, 10);
    return isFinite(cents) && cents > 0 ? cents / 100 : null;
  };

  /** auction.auction_id from hidden product map when URL/metafield id is missing. */
  window.staResolveAuctionIdFromMap = function (productId) {
    if (productId == null || productId === '') return '';
    var id = String(productId).replace(/"/g, '').trim();
    var li = document.querySelector(
      'ul.urls-check-cards-products li[data-product-id="' + id + '"]'
    );
    if (!li) return '';
    return String(li.getAttribute('data-auction-api-id') || '').trim();
  };

  /**
   * Resolve display price: current → starting → initial → optional store fallback.
   * Returns null when no usable value exists.
   */
  window.staAuctionDisplayPrice = function (attrs, storeDollars) {
    var a = attrs || {};
    var p = null;
    if (a.current_price != null && a.current_price !== '') p = Number(a.current_price);
    if ((p == null || !isFinite(p)) && a.starting_price != null && a.starting_price !== '') {
      p = Number(a.starting_price);
    }
    if ((p == null || !isFinite(p)) && a.initial_price != null && a.initial_price !== '') {
      p = Number(a.initial_price);
    }
    if ((p == null || !isFinite(p)) && storeDollars != null && isFinite(storeDollars)) {
      p = Number(storeDollars);
    }
    return p != null && isFinite(p) ? p : null;
  };

  /** Label for live lot cards / PDP when bid count is known. */
  window.staAuctionLivePriceLabel = function (bids) {
    return (parseInt(bids, 10) || 0) > 0 ? 'Current bid:' : 'Starting bid:';
  };

  /**
   * Resolve auction id: URL → product-info metafield → hidden map → optional API lookup.
   */
  window.staResolveAuctionIdAsync = function (productId) {
    var syncId = window.staResolveAuctionId ? window.staResolveAuctionId() : '';
    if (syncId) return Promise.resolve(syncId);

    var mapId = window.staResolveAuctionIdFromMap
      ? window.staResolveAuctionIdFromMap(productId)
      : '';
    if (mapId) return Promise.resolve(mapId);

    if (!productId || !window.staFetchAuctionsByProductIds) {
      return Promise.resolve('');
    }

    return window.staFetchAuctionsByProductIds([productId], { perPage: 1 })
      .then(function (payload) {
        var rows = window.staPickAuctionArray
          ? window.staPickAuctionArray(payload)
          : (payload && payload.data) || [];
        if (!rows.length) return '';
        var row = rows[0];
        return String((row && row.id) || '').trim();
      })
      .catch(function () {
        return '';
      });
  };
})();
